<template>
  <div class="product-upload-container">
    <el-card class="main-card">
      <div slot="header" class="card-header">
        <span class="title">{{ isEdit ? '产品编辑' : '产品上传管理' }}</span>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">{{ isEdit ? '保存修改' : '提交产品' }}</el-button>
      </div>

      <el-form ref="productForm" :model="productForm" :rules="rules" label-width="100px" class="product-form">
        <!-- 基础信息 -->
        <el-form-item label="产品名称" prop="name">
          <el-input v-model="productForm.name" placeholder="请输入产品名称" style="width: 100%"></el-input>
        </el-form-item>

        <!-- 产品价格 -->
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="产品价格" prop="price">
              <el-input v-model="productForm.price" placeholder="0.00" @change="generateSkuList(true)">
                <template slot="prepend">¥</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
          </el-col>
          <el-col :span="8">
          </el-col>
        </el-row>

        <!-- 购买链接 -->
        <el-form-item label="购买链接" prop="buyUrl">
          <el-input v-model="productForm.buyUrl" placeholder="请输入购买链接" style="width: 100%"></el-input>
        </el-form-item>

        <!-- 主图上传 -->
        <el-form-item label="产品主图" prop="mainImage">
          <div class="main-image-upload">
            <div v-if="mainImageBlob" class="main-image-preview">
              <img :src="mainImageBlob" alt="主图">
              <div class="image-actions">
                <el-button icon="el-icon-delete" circle @click="removeMainImage"></el-button>
              </div>
            </div>
            <div v-else class="upload-area" @click="triggerMainImageUpload">
              <i class="el-icon-plus"></i>
              <div>点击上传主图</div>
            </div>
            <input
                type="file"
                ref="mainImageInput"
                style="display: none"
                accept="image/*"
                @change="handleMainImageUpload"
            >
            <div class="upload-tip">建议尺寸：800×800px，大小不超过2MB</div>
          </div>
        </el-form-item>

        <!-- SKU配置 -->
        <el-form-item label="SKU配置" prop="skuList">
          <div class="sku-config-container">
            <div class="sku-specs">
              <div class="sku-spec-item" v-for="(spec, specIndex) in productForm.specs" :key="specIndex">
                <div class="spec-header">
                  <el-input
                      v-model="spec.name"
                      placeholder="规格名称（如颜色）"
                      style="width: 150px"
                  ></el-input>
                  <el-button
                      type="text"
                      icon="el-icon-delete"
                      @click="removeSpec(specIndex)"
                      v-if="productForm.specs.length > 1"
                  ></el-button>
                </div>
                <div class="spec-values">
                  <el-tag
                      v-for="(value, valueIndex) in spec.values"
                      :key="valueIndex"
                      closable
                      @close="removeSpecValue(specIndex, valueIndex)"
                  >
                    {{ value }}
                  </el-tag>
                  <el-input
                      v-if="spec.inputVisible"
                      v-model="spec.inputValue"
                      ref="saveTagInput"
                      size="small"
                      style="width: 100px;"
                      @keyup.enter.native="handleSpecValueConfirm(specIndex)"
                      @blur="handleSpecValueConfirm(specIndex)"
                  >
                  </el-input>
                  <el-button
                      v-else
                      size="small"
                      @click="showSpecValueInput(specIndex)"
                  >+ 添加</el-button>
                </div>
              </div>
              <el-button
                  type="text"
                  icon="el-icon-plus"
                  @click="addSpec"
              >添加规格</el-button>
            </div>

            <div class="sku-table" v-if="productForm.skuList.length > 0">
              <el-table
                  :data="productForm.skuList"
                  border
                  style="width: 100%"
                  class="sku-table-content"
              >
                <el-table-column
                    v-for="spec in productForm.specs"
                    :key="spec.name"
                    :label="spec.name"
                    :prop="spec.name"
                    width="120"
                >
                  <template slot-scope="scope">
                    {{ scope.row.specs[spec.name] }}
                  </template>
                </el-table-column>
                <el-table-column label="价格" width="150">
                  <template slot-scope="scope">
                    <el-input
                        v-model="scope.row.price"
                        size="small"
                        @change="calculateTotalStock"
                    >
                      <template slot="prepend">¥</template>
                    </el-input>
                  </template>
                </el-table-column>
                <el-table-column label="库存" width="150">
                  <template slot-scope="scope">
                    <el-input
                        v-model="scope.row.stock"
                        size="small"
                        type="number"
                        min="0"
                        @change="calculateTotalStock"
                    ></el-input>
                  </template>
                </el-table-column>
                <el-table-column label="SKU编码" width="180">
                  <template slot-scope="scope">
                    <el-input
                        v-model="scope.row.skuCode"
                        size="small"
                        placeholder="自动生成"
                    ></el-input>
                  </template>
                </el-table-column>
              </el-table>
              <div class="total-stock">总库存：{{ totalStock }}</div>
            </div>
            <div v-else class="no-sku-tip">
              请先添加规格并设置规格值
            </div>
          </div>
        </el-form-item>

        <!-- 详情图批量上传 -->
        <el-form-item label="详情图片" prop="detailImages">
          <div class="detail-images-upload">
            <div class="upload-controls">
              <el-upload
                  class="upload-demo"
                  action="#"
                  :on-change="handleDetailImagesChange"
                  :on-remove="handleDetailImageRemove"
                  :file-list="detailImageFileSet"
                  :auto-upload="false"
                  multiple
                  list-type="picture-card"
                  :before-upload="beforeDetailImageUpload"
                  accept="image/*"
                  ref="detailUpload"
              >
                <i class="el-icon-plus"></i>
                <div slot="tip" class="el-upload__tip">
                  <p>建议尺寸：750px宽度，高度自适应</p>
                  <p>支持格式：JPG、PNG、GIF，每张图片不超过5MB</p>
                </div>
              </el-upload>
            </div>

            <!-- 已上传图片列表（可拖拽排序） -->
            <div class="detail-images-list" v-if="detailImageBlobSet.length > 0">
              <div class="list-header">
                <span>已选择图片 ({{ detailImageBlobSet.length }})</span>
                <p>支持拖拽排序</p>
                <el-button
                    type="text"
                    @click="clearAllDetailImages"
                    :disabled="detailImageBlobSet.length === 0"
                >清空全部</el-button>
              </div>
              <draggable
                  v-model="detailImageBlobSet"
                  class="draggable-list"
                  @end="onDragEnd"
                  handle=".drag-handle"
              >
                <div
                    v-for="(image, index) in detailImageBlobSet"
                    :key="index"
                    class="detail-image-item"
                >
                  <div class="image-wrapper">
                    <img :src="image.url" :alt="`详情图${index + 1}`">
                    <div class="image-overlay">
                      <div class="image-index">{{ index + 1 }}</div>
                      <div class="image-actions">
                        <i class="el-icon-rank drag-handle"></i>
                        <i class="el-icon-delete" @click="removeDetailImage(index)"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </draggable>
            </div>
          </div>
        </el-form-item>

        <!-- 富文本编辑器 -->
        <el-form-item label="产品详情" prop="description">
          <div class="editor-container">
            <editor
                v-model="productForm.description"
                id="uuid"
                apiKey="no-api-key"
                :init="{
                license_key: 'gpl',
                // 去除tinyMCE的logo
                branding: false,
                // 去除右上角的按钮
                promotion: false,
                language_url: '/tinymce/langs/zh_CN.js',
                // 设置中文语言
                language: 'zh_CN',
                // 设置中文语言包路径
                plugins: 'advlist anchor autolink charmap code fullscreen help image insertdatetime link lists media preview searchreplace table visualblocks wordcount',
                toolbar: 'undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image',
                height: 500,
              }"
                :tinymceScriptSrc="tinymceScriptSrc"
            />
            <div class="editor-tips">
              <p>提示：可以添加文字、图片、表格等内容，建议详细描述产品特性</p>
            </div>
          </div>
        </el-form-item>

        <!-- 提交按钮 -->
        <el-form-item>
          <div class="form-actions">
            <el-button type="primary" @click="handleSubmit" :loading="submitting">{{ isEdit ? '保存修改' : '提交产品' }}</el-button>
            <el-button @click="handleReset">{{ isEdit ? '恢复原数据' : '重置表单' }}</el-button>
          </div>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'

import draggable from 'vuedraggable'
import {baseUrl} from "@/config";
import {addProduct, getProductById, updateProduct, uploadFile} from "@/api";

export default {
  name: 'ProductUpload',
  components: {
    Editor,
    draggable
  },
  data() {
    let colorSpec =  {
      name: '颜色',
      values: ['象牙白', '香槟金', '星空灰'],
      inputVisible: false,
      inputValue: ''
    }
    let specs = [Object.assign({}, colorSpec)];
    return {
      productId: null,// 编辑模式下的产品ID，为空表示新增模式
      colorSpec: colorSpec,
      productForm: {
        name: '',
        buyUrl: '',
        // category: '',
        mainImage: '',
        price: undefined,
        // originalPrice: '',
        // weight: '',
        specs: specs,
        skuList: [],
        detailImages: [],
        description: ''
      },
      rules: {
        name: [
          { required: true, message: '请输入产品名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        // category: [
        //   { required: true, message: '请选择产品分类', trigger: 'change' }
        // ],
        mainImage: [
          { required: true, message: '请上传产品主图', trigger: 'change' }
        ],
        price: [
          { required: true, message: '请输入产品价格', trigger: 'blur' },
          { pattern: /^\d+(\.\d{1,2})?$/, message: '请输入正确的价格格式', trigger: 'blur' }
        ]
      },
      mainImageBlob: '',
      detailImageBlobSet: [],
      detailImageFileSet: [],
      totalStock: 0,
      submitting: false,
      // savingDraft: false,
      // previewVisible: false
      tinymceScriptSrc: `${process.env.VUE_APP_TINYMCE_URL}/tinymce/tinymce.min.js`
    }
  },
  computed: {
    isEdit() {
      return !!this.productId
    }
  },
  watch: {
    'productForm.specs': {
      handler() {
        this.generateSkuList()
      },
      deep: true
    },
    // 同路由组件下通过菜单切换（如编辑页点"添加产品"），路由参数变化时重新初始化
    '$route'(to, from) {
      if (to.query.id !== from.query.id) {
        if (to.query.id) {
          this.productId = to.query.id
          this.fetchProductDetail(to.query.id)
        } else {
          this.productId = null
          this.handleReset()
        }
      }
    }
  },
  mounted() {
    const id = this.$route.query.id
    if (id) {
      this.productId = id
      this.fetchProductDetail(id)
    } else {
      this.generateSkuList()
    }
  },
  methods: {
    // 主图上传相关方法
    triggerMainImageUpload() {
      this.$refs.mainImageInput.click()
    },

    handleMainImageUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      // 验证文件大小
      if (file.size > 2 * 1024 * 1024) {
        this.$message.error('主图大小不能超过2MB')
        return
      }

      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        this.$message.error('请上传图片文件')
        return
      }

      // 创建预览URL
      const reader = new FileReader()
      reader.onload = (e) => {
        this.productForm.mainImage = file.name
        this.mainImageBlob = e.target.result
        this.$refs.productForm.clearValidate('mainImage')
      }
      reader.readAsDataURL(file)

      // 清空input值，允许重复上传同一文件
      event.target.value = ''
    },

    removeMainImage() {
      this.productForm.mainImage = ''
      this.mainImageBlob = ''
    },

    // SKU配置相关方法
    addSpec() {
      this.productForm.specs.push({
        name: '',
        values: [],
        inputVisible: false,
        inputValue: ''
      })
    },

    removeSpec(index) {
      this.productForm.specs.splice(index, 1)
    },

    showSpecValueInput(specIndex) {
      this.productForm.specs[specIndex].inputVisible = true
      this.$nextTick(() => {
        this.$refs.saveTagInput[specIndex].focus()
      })
    },

    handleSpecValueConfirm(specIndex) {
      const spec = this.productForm.specs[specIndex]
      const inputValue = spec.inputValue.trim()

      if (inputValue && !spec.values.includes(inputValue)) {
        spec.values.push(inputValue)
      }

      spec.inputVisible = false
      spec.inputValue = ''
    },

    removeSpecValue(specIndex, valueIndex) {
      this.productForm.specs[specIndex].values.splice(valueIndex, 1)
    },

    // 生成SKU列表
    generateSkuList(modified) {
      // 如果没有规格，清空SKU列表
      if (this.productForm.specs.length === 0 ||
          this.productForm.specs.some(spec => !spec.name || spec.values.length === 0)) {
        this.productForm.skuList = []
        this.totalStock = 0
        return
      }

      // 获取所有规格值组合
      const combinations = this.getCombinations()

      // 生成SKU列表
      this.productForm.skuList = combinations.map(combo => {
        const specs = {}
        let skuName = ''

        this.productForm.specs.forEach((spec, index) => {
          specs[spec.name] = combo[index]
          skuName += skuName ? `-${combo[index]}` : combo[index]
        })

        // 查找是否已存在相同规格的SKU，保留原有数据
        const existingSku = this.productForm.skuList.find(sku =>
            JSON.stringify(sku.specs) === JSON.stringify(specs)
        )
        let price = existingSku ? existingSku.price : this.productForm.price || '0.00';
        if(modified) {
          price = this.productForm.price;
        }
        return {
          specs,
          skuName,
          price: price,
          stock: existingSku ? existingSku.stock : '0',
          skuCode: existingSku ? existingSku.skuCode : this.generateSkuCode()
        }
      })

      this.calculateTotalStock()
    },

    // 获取所有规格组合
    getCombinations() {
      const result = []
      const specs = this.productForm.specs

      // 递归生成组合
      function generate(index, current) {
        if (index === specs.length) {
          result.push([...current])
          return
        }

        const spec = specs[index]
        for (const value of spec.values) {
          current.push(value)
          generate(index + 1, current)
          current.pop()
        }
      }

      generate(0, [])
      return result
    },

    // 生成SKU编码
    generateSkuCode() {
      return 'SKU' + Date.now().toString().slice(-8) + Math.random().toString(36).substr(2, 4).toUpperCase()
    },

    // 计算总库存
    calculateTotalStock() {
      this.totalStock = this.productForm.skuList.reduce((total, sku) => {
        return total + (parseInt(sku.stock) || 0)
      }, 0)
    },

    // 详情图上传相关方法
    beforeDetailImageUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt50M = file.size / 1024 / 1024 < 50 // 与后端上传接口 50MB 上限保持一致

      if (!isImage) {
        this.$message.error('只能上传图片文件')
      }
      if (!isLt50M) {
        this.$message.error('图片大小不能超过50MB')
      }

      return isImage && isLt50M
    },

    handleDetailImagesChange(file, fileList) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.detailImageBlobSet.push({
          name: file.name,
          url: e.target.result,
          uid: file.uid
        })
      }
      reader.readAsDataURL(file.raw)

      this.detailImageFileSet = fileList
    },

    handleDetailImageRemove(file, fileList) {
      this.detailImageFileSet = fileList
      const index = this.detailImageBlobSet.findIndex(img => img.uid === file.uid)
      if (index !== -1) {
        this.detailImageBlobSet.splice(index, 1)
      }
    },

    removeDetailImage(index) {
      this.detailImageBlobSet.splice(index, 1)
      // 同时从upload组件中移除
      const uid = this.detailImageBlobSet[index]?.uid
      if (uid) {
        this.detailImageFileSet = this.detailImageFileSet.filter(file => file.uid !== uid)
      }
    },

    clearAllDetailImages() {
      this.detailImageBlobSet = []
      this.detailImageFileSet = []
    },

    // 拖拽排序结束
    onDragEnd() {
      // console.log('图片顺序已更新')
    },

    async uploadImage(fileName, base64) {
      const byteString = atob(base64.split(",")[1]);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }
      let imageFile = new File([arrayBuffer], fileName, {type: "image/png"});

      const formData = new FormData();
      formData.append("file", imageFile);
      formData.append('userId', window.localStorage.getItem('SET_NAME'));// 获取storage中SET_NAME的值
      formData.append('type', 'product');

      const response = await uploadFile(formData);
      if (response.code === 0) {
        return `${baseUrl}/v1/getLatestImage?fileName=${response.data}`;
      } else {
        throw new Error(response.message || '图片上传失败');
      }
    },

    // 编辑模式：拉取产品详情并回填表单
    async fetchProductDetail(id) {
      try {
        const response = await getProductById(id)
        if (response.code !== 0) {
          throw new Error(response.message || '获取产品详情失败')
        }
        const p = response.data

        this.productForm.name = p.name || ''
        this.productForm.buyUrl = p.buyUrl || ''
        this.productForm.price = p.price
        this.productForm.description = p.description || ''
        this.productForm.mainImage = p.mainImage || ''
        // 主图回填：已有线上图片直接作为预览，不重新上传
        this.mainImageBlob = p.mainImage || ''

        // 详情图回填：已有线上图片保留原地址
        this.productForm.detailImages = p.detailImages || []
        this.detailImageBlobSet = (p.detailImages || []).map((url, index) => ({
          name: `详情图${index + 1}`,
          url
        }))
        this.detailImageFileSet = []

        // skuList / specs 后端以JSON字符串存储，回填时需解析
        this.productForm.skuList = p.skuList ? JSON.parse(p.skuList) : []
        const specs = p.specs ? JSON.parse(p.specs) : []
        this.productForm.specs = specs.length > 0
            // 补充输入框控制字段，保证规格值交互可用
            ? specs.map(spec => Object.assign({inputVisible: false, inputValue: ''}, spec))
            : [Object.assign({}, this.colorSpec)]
        this.calculateTotalStock()
        // 清除回填可能触发的校验提示
        this.$nextTick(() => {
          this.$refs.productForm && this.$refs.productForm.clearValidate()
        })
      } catch (error) {
        this.$message.error('获取产品详情失败: ' + error.message)
      }
    },

    // 表单操作
    handleSubmit() {
      this.$refs.productForm.validate(async (valid) => {
        if (valid) {
          this.submitting = true
          // 主图：编辑模式下未更换的图片（线上地址）不重复上传
          if (this.mainImageBlob && this.mainImageBlob.startsWith('http')) {
            this.productForm.mainImage = this.mainImageBlob;
          } else {
            try {
              this.productForm.mainImage = await this.uploadImage('mainImage.png', this.mainImageBlob);
            } catch (error) {
              this.submitting = false
              this.$message.error('主图片上传失败: ' + error.message);
              return false;
            }
          }
          try {
            let detailImage = [];
            for (let i in this.detailImageBlobSet) {
              let image = this.detailImageBlobSet[i];
              // 已有线上地址的详情图保留原地址，仅新选的图片需要上传
              if (image.url && image.url.startsWith('http')) {
                detailImage.push(image.url);
              } else {
                detailImage.push(
                    await this.uploadImage(`detailImage${parseInt(i) + 1}.png`, image.url)
                );
              }
            }
            this.productForm.detailImages = detailImage;
          } catch (error) {
            this.submitting = false
            this.$message.error('详情图上传失败: ' + error.message);
            return false;
          }
          try {
            let params = Object.assign({}, this.productForm);
            if(params.specs) {
              for(let i in params.specs) {
                delete params.specs[i].inputVisible;
                delete params.specs[i].inputValue;
              }
              params.specs = JSON.stringify(params.specs);
            }
            if(params.skuList) {
              params.skuList = JSON.stringify(params.skuList);
            }
            const response = this.isEdit
                ? await updateProduct(Object.assign(params, {id: this.productId}))
                : await addProduct(params);
            if (response.code === 0) {
              this.$message.success(this.isEdit ? '产品修改成功！' : '产品提交成功！')

              this.submitting = false;
              // this.previewVisible = true
              this.$router.push({name: "Products" });
              return true;
            }
            this.submitting = false
            this.$message.error((this.isEdit ? '修改失败: ' : '新增失败: ') + response.message);
            return true;
          } catch (error) {
            this.submitting = false
            this.$message.error((this.isEdit ? '修改失败: ' : '新增失败: ') + error.message);
          }
        }
        else {
          this.$message.error('请检查表单填写是否完整')
          return false
        }
      })
    },

    handleReset() {
      // 编辑模式下恢复为后端原数据
      if (this.isEdit) {
        this.fetchProductDetail(this.productId)
        return
      }
      this.$refs.productForm.resetFields()
      this.productForm.specs = [Object.assign({}, this.colorSpec)]
      this.productForm.skuList = []
      this.productForm.detailImages = []
      this.detailImageFileSet = []
      this.totalStock = 0
    },
  }
}
</script>

<style scoped>
.product-upload-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.main-card {
  max-width: 1200px;
  margin: 0 auto;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header .title {
  font-size: 20px;
  font-weight: bold;
}

.product-form {
  padding: 20px;
}

/* 主图上传样式 */
.main-image-upload {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.upload-area {
  width: 200px;
  height: 200px;
  border: 2px dashed #dcdfe6;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #909399;
  transition: border-color 0.3s;
}

.upload-area:hover {
  border-color: #409eff;
}

.upload-area i {
  font-size: 40px;
  margin-bottom: 10px;
}

.main-image-preview {
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 6px;
  overflow: hidden;
}

.main-image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-actions {
  position: absolute;
  top: 10px;
  right: 10px;
}

.upload-tip {
  margin-top: 10px;
  color: #909399;
  font-size: 12px;
}

/* SKU配置样式 */
.sku-config-container {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
}

.sku-specs {
  margin-bottom: 20px;
}

.sku-spec-item {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #e4e7ed;
}

.sku-spec-item:last-child {
  border-bottom: none;
}

.spec-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.spec-values {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.sku-table-content {
  margin-top: 20px;
}

.total-stock {
  margin-top: 10px;
  padding: 10px;
  background-color: #f5f7fa;
  text-align: right;
  font-weight: bold;
}

.no-sku-tip {
  text-align: center;
  padding: 40px;
  color: #909399;
}

/* 详情图上传样式 */
.detail-images-upload {
  width: 100%;
}

.upload-controls {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.upload-demo {
  flex: 1;
}

.upload-tips {
  flex: 1;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.detail-images-list {
  margin-top: 20px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.draggable-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
}

.detail-image-item {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.3s;
}

.detail-image-item:hover {
  transform: translateY(-5px);
}

.image-wrapper {
  position: relative;
  width: 100%;
  height: 150px;
}

.image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px;
  opacity: 0;
  transition: opacity 0.3s;
}

.detail-image-item:hover .image-overlay {
  opacity: 1;
}

.image-index {
  color: white;
  font-weight: bold;
  background: rgba(0,0,0,0.5);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.image-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.image-actions i {
  color: white;
  cursor: pointer;
  background: rgba(0,0,0,0.5);
  padding: 5px;
  border-radius: 50%;
  font-size: 14px;
}

.drag-handle {
  cursor: move;
}

/* 富文本编辑器样式 */
.editor-container {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.editor-tips {
  padding: 10px;
  background-color: #f5f7fa;
  color: #909399;
  font-size: 12px;
  border-top: 1px solid #dcdfe6;
}

/* 表单操作按钮 */
.form-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

/* 预览样式 */
.preview-content {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 10px;
}

.preview-main-image {
  width: 100%;
  max-width: 400px;
  border-radius: 8px;
  overflow: hidden;
}

.preview-main-image img {
  width: 100%;
  height: auto;
}

.no-image {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  color: #909399;
  border-radius: 8px;
}

.preview-price {
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 15px;
}

.current-price {
  font-size: 28px;
  color: #f56c6c;
  font-weight: bold;
}

.original-price {
  font-size: 18px;
  color: #909399;
  text-decoration: line-through;
}

.preview-detail-images h3,
.preview-description h3 {
  margin: 25px 0 15px;
  color: #333;
  border-left: 4px solid #409eff;
  padding-left: 10px;
}

.preview-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.preview-images-grid img {
  width: 100%;
  height: auto;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.preview-description {
  line-height: 1.6;
}

.preview-description >>> img {
  max-width: 100%;
  height: auto;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .upload-controls {
    flex-direction: column;
  }

  .draggable-list {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }

  .form-actions {
    flex-direction: column;
  }

  .form-actions .el-button {
    width: 100%;
  }
}
</style>
